// AGC: NewPlotStep1 - Create plot - basic info (NAME ONLY)
// AGC: Modified to only require field name
// AGC: Area is calculated from geometry, crop is selected after geometry
import React, { useState } from 'react'
import { TopBar, GlassCard, Button } from '../shared'
import type { Screen, PlotFormData } from '../../types'

interface NewPlotStep1Props {
  initialData?: Partial<PlotFormData>
  onNavigate: (screen: Screen, data?: { plotData?: PlotFormData }) => void
  onBack: () => void
}

/**
 * AGC: NewPlotStep1 - Form for basic plot information
 * AGC: MODIFIED: Only requires field name
 * AGC: Area will be calculated from geometry in next steps
 * AGC: Crop selection happens after geometry is defined
 */
export const NewPlotStep1: React.FC<NewPlotStep1Props> = ({ initialData, onNavigate, onBack }) => {
  const [formData, setFormData] = useState<Partial<PlotFormData>>({
    name: initialData?.name || '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  // AGC: Validate form - only name is required now
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name?.trim()) {
      newErrors.name = 'Nome é obrigatório'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // AGC: Handle next step - go to geometry method selection
  const handleNext = () => {
    if (validate()) {
      onNavigate('geometry-method', { 
        plotData: {
          name: formData.name || '',
          area: 0, // AGC: Will be calculated from geometry
          cropType: '', // AGC: Will be selected after geometry
        } as PlotFormData 
      })
    }
  }

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* AGC: Top Bar */}
      <TopBar 
        title="Novo Talhão"
        subtitle="Passo 1 de 4"
        onBack={onBack}
      />

      {/* AGC: Progress indicator */}
      <div className="px-4 py-3">
        <div className="flex gap-2">
          <div className="flex-1 h-1 rounded-full bg-[#0B5F4A]" />
          <div className="flex-1 h-1 rounded-full bg-gray-200" />
          <div className="flex-1 h-1 rounded-full bg-gray-200" />
          <div className="flex-1 h-1 rounded-full bg-gray-200" />
        </div>
      </div>

      {/* AGC: Form */}
      <div className="px-4 py-4 space-y-4">
        {/* AGC: Name Input - Only required field */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nome do Lote / Nome do Campo *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ex: Talhão Norte A"
            className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0B5F4A] transition-all ${
              errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'
            }`}
            data-testid="field-name-input"
          />
          {errors.name && (
            <p className="text-red-500 text-sm mt-1">{errors.name}</p>
          )}
        </div>

        {/* AGC: Info Card - Explain next steps */}
        <GlassCard className="bg-blue-50 border-blue-200">
          <div className="flex gap-3">
            <span className="text-xl">💡</span>
            <div>
              <p className="font-medium text-blue-800">Próximos passos</p>
              <p className="text-sm text-blue-700 mt-1">
                No próximo passo, você escolherá como definir os limites do talhão:
              </p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• <strong>GPS:</strong> Caminhe pelo limite usando o GPS</li>
                <li>• <strong>Mapa:</strong> Desenhe os vértices na imagem de satélite</li>
              </ul>
              <p className="text-sm text-blue-700 mt-2">
                A área será calculada automaticamente após definir a geometria.
              </p>
            </div>
          </div>
        </GlassCard>

        {/* AGC: Additional guidance */}
        <GlassCard className="bg-emerald-50 border-emerald-200">
          <div className="flex gap-3">
            <span className="text-xl">🌾</span>
            <div>
              <p className="font-medium text-emerald-800">Seleção de Cultura</p>
              <p className="text-sm text-emerald-700">
                Após definir a geometria do talhão, o sistema sugerirá culturas 
                recomendadas com base na região e época do ano.
              </p>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* AGC: Footer with button - safe-area-bottom for Android nav bar */}
      <div
        className="fixed bottom-0 left-0 right-0 px-4 pt-4 bg-white border-t border-gray-200"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
      >
        <Button fullWidth onClick={handleNext}>
          Avançar
        </Button>
      </div>
    </div>
  )
}

export default NewPlotStep1
